import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "@/assets/logo-quartix.io-branco.png";

const QUARTIX_DARK_YELLOW = [217, 143, 0]; // #D98F00
const QUARTIX_YELLOW = [255, 176, 0]; // #FFB000
const QUARTIX_LIGHT_GRAY = [21, 23, 28];
const QUARTIX_GRAY = [14, 15, 18];
const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];

const PAGE_WIDTH = 145;

const BASE_FONT_SIZE = 10;
const BASE_PADDING_Y = 2;
const BASE_MIN_HEIGHT = 5.2;

const ID_ROW_PADDING_Y = 6;

async function imageToBase64(src) {
  const response = await fetch(src);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });
}

export async function loadLogoForPDF() {
  return imageToBase64(logo);
}

function zebraColor(count) {
  return count % 2 !== 0 ? QUARTIX_LIGHT_GRAY : QUARTIX_GRAY;
}

function formatBRNumber(value) {
  if (value == null || value === "-" || isNaN(Number(value))) return "-";

  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatRate(value) {
  if (value == null || isNaN(Number(value))) return "-";

  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

function renderTable(doc, rows, imgData, tableWidth) {
  let zebraCount = 0;
  let lastZebraRowIndex = -1;

  doc.autoTable({
    startY: 0,
    margin: {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    tableWidth,
    theme: "plain",
    body: rows,

    pageBreak: "avoid",
    rowPageBreak: "avoid",
    showHead: "never",
    showFoot: "never",

    styles: {
      font: "helvetica",
      fontSize: BASE_FONT_SIZE,
      textColor: WHITE,
      cellPadding: {
        top: BASE_PADDING_Y,
        right: 5,
        bottom: BASE_PADDING_Y,
        left: 5,
      },
      minCellHeight: BASE_MIN_HEIGHT,
      valign: "middle",
      lineWidth: 0,
      overflow: "ellipsize",
    },

    columnStyles: {
      0: { cellWidth: tableWidth * 0.42 },
      1: { cellWidth: tableWidth * 0.16 },
      2: { cellWidth: tableWidth * 0.12 },
      3: { cellWidth: tableWidth * 0.3 },
    },

    didParseCell(hookData) {
      if (hookData.section !== "body") return;

      if (!hookData.cell.raw?.styles?.fillColor) {
        if (hookData.row.index !== lastZebraRowIndex) {
          zebraCount += 1;
          lastZebraRowIndex = hookData.row.index;
        }

        hookData.cell.styles.fillColor = zebraColor(zebraCount);
      }

      hookData.cell.styles.lineWidth = 0.01;
      hookData.cell.styles.lineColor = hookData.cell.styles.fillColor;
    },

    didDrawCell(hookData) {
      if (hookData.section !== "body") return;

      const isLastRow = hookData.row.index === hookData.table.body.length -1;
      const isFirstCell = hookData.column.index === 0;

      if (!isLastRow || !isFirstCell) return;

      const cell = hookData.cell;

      doc.addImage(imgData, "PNG", cell.x + 5, cell.y+5, 28, 4.8);
    },
  });
}

export async function generateImportacaoPDF({
  data,
  fileName = "importacao",
  preview = false,
}) {
  const now = new Date();

  const saveDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getDate()).padStart(2, "0")}`;

  const imgData = await loadLogoForPDF();

  const tableWidth = PAGE_WIDTH;

  const amount = Number(data.rfq_amount_number ?? data.rfq_amount);
  const rate = Number(data.rfq_deal_px);
  const valorReais = amount * rate;

  const rows = [
    [
      {
        content: String(data.rfq_security || "Importação"),
        colSpan: 4,
        styles: {
          fillColor: QUARTIX_YELLOW,
          textColor: BLACK,
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
          fontSize: 10,
          lineWidth: 0,
          cellPadding: {
            top: 5,
            right: 0,
            bottom: 5,
            left: 0,
          },
        },
      },
    ],

    [
      {
        content: data.rfq_ccy || "-",
        colSpan: 2,
        styles: {
          fillColor: BLACK,
          textColor: WHITE,
          fontStyle: "bold",
          halign: "center",
          lineWidth: 0,
        },
      },
      {
        content: data.ccyName || "Dólar Americano",
        colSpan: 2,
        styles: {
          fillColor: BLACK,
          textColor: WHITE,
          fontStyle: "bold",
          halign: "center",
          lineWidth: 0,
        },
      },
    ],

    [
      {
        content: "Banco",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content: data.bank_label || "-",
        styles: { halign: "right", lineWidth: 0 },
      },
    ],
    [
      {
        content: "Status",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content: data.rfq_status || "-",
        styles: { halign: "right",  lineWidth: 0, },
      },
    ],

    [
      {
        content: "Lado",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content: data.rfq_side || "-",
        styles: { halign: "right", lineWidth: 0, textColor: data.rfq_side === "BUY" ? "#00c853" : "#FF6D00", },
      },
    ],

/*
    [
      {
        content: "Valor",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content: formatBRNumber(amount),
        styles: { halign: "right", lineWidth: 0, fontSize:10 },
      },
    ],
*/
    [
      {
        content: "Liquidação",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content:
          data.rfq_security === "FXSPOT"
            ? data.rfq_spotsett || "-"
            : data.rfq_maturity || "-",
        styles: { halign: "right", lineWidth: 0 },
      },
    ],

    [
      {
        content: "Taxa (CCY)",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content: `${formatRate(rate)}`,
        styles: { halign: "right", lineWidth: 0 },
      },
    ],

    [
      {
        content: "Valor em Reais" ,
        colSpan: 2,
        styles: { fontStyle: "bold", lineWidth: 0 },
      },
      {
        content: "R$",
        styles: { halign: "center", fontStyle: "bold", lineWidth: 0 },
      },
      {
        content: formatBRNumber(valorReais),
        styles: { halign: "right", fontStyle: "bold", fontSize: 12, lineWidth: 0 },
      },
    ],/*

    [
      {
        content: "Imposto de Renda",
        styles: { lineWidth: 0 },
      },
      {
        content: `${formatBRNumber(data.impostoRendaPercentual || 0)}%`,
        styles: { halign: "right", lineWidth: 0 },
      },
      {
        content: "R$",
        styles: { halign: "center", lineWidth: 0 },
      },
      {
        content: formatBRNumber(data.impostoRendaValor),
        styles: { halign: "right", lineWidth: 0 },
      },
    ],

    [
      {
        content: "IOF",
        styles: { lineWidth: 0 },
      },
      {
        content: `${formatBRNumber(data.iofPercentual || 0)}%`,
        styles: { halign: "right", lineWidth: 0 },
      },
      {
        content: "R$",
        styles: { halign: "center", lineWidth: 0 },
      },
      {
        content: formatBRNumber(data.iofValor),
        styles: { halign: "right", lineWidth: 0 },
      },
    ],

    [
      {
        content: "Despesa",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content: formatBRNumber(data.despesa),
        styles: { halign: "right", lineWidth: 0 },
      },
    ],

    [
      {
        content: "Taxa",
        colSpan: 3,
        styles: { lineWidth: 0 },
      },
      {
        content: formatRate(data.taxaDespesa || data.taxa),
        styles: { halign: "right", lineWidth: 0 },
      },
    ],

    [
      {
        content: "Total para Pagamento",
        colSpan: 2,
        styles: {
          fillColor: QUARTIX_GRAY,
          textColor: WHITE,
          fontStyle: "bold",
          lineWidth: 0,
        },
      },
      {
        content: "R$",
        styles: {
          fillColor: QUARTIX_GRAY,
          textColor: WHITE,
          fontStyle: "bold",
          halign: "center",
          lineWidth: 0,
        },
      },
      {
        content: formatBRNumber(valorReais),
        styles: {
          fillColor: QUARTIX_GRAY,
          textColor: WHITE,
          fontStyle: "bold",
          halign: "right",
          lineWidth: 0,
        },
      },
    ],*/

    [
      {
        content: "Data:",
        colSpan: 3,
        styles: {
          halign: "start",
          //fontStyle: "bold",
          lineWidth: 0,
          font: "helvetica",
          fontSize: 8.2,
          textColor: WHITE,
          cellPadding: {
            top: 2,
            right: 0,
            bottom: 2,
            left: 5,
          },
        },
      },
      {
        content: data.rfq_deal_time || "-",
        colSpan: 1,
        styles: {
          halign: "end",
         // fontStyle: "bold",
          lineWidth: 0,
          font: "helvetica",
          fontSize: 10,
          textColor: WHITE,
          cellPadding: {
            top: 1,
            right: 0,
            bottom: 1,
            left: 12,
          },
          minCellHeight: 5.2,
        },
      },
    ],

    [
      {
        content:`ID:  ${String(data.rfq_id || "-")}`,
        colSpan: 4,
        styles: {
          fillColor: BLACK,
          textColor: WHITE,
          fontStyle: "bold",
          halign: "right",
          valign: "middle",
          fontSize: 8,
          lineWidth: 0,
          cellPadding: {
            top: ID_ROW_PADDING_Y,
            right: 6,
            bottom: ID_ROW_PADDING_Y,
            left: 6,
          },
        },
      },
    ],
  ];

  /**
   * First render: measure the real autoTable height.
   * Use portrait here so [PAGE_WIDTH, 300] is truly width 145 / height 300.
   */
  const measureDoc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [PAGE_WIDTH, 300],
  });

  renderTable(measureDoc, rows, imgData, tableWidth);

  const pageHeight = measureDoc.lastAutoTable.finalY + 0.5;

  /**
   * Final render: exact page height, so no second page and no extra whitespace.
   */
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [PAGE_WIDTH, pageHeight],
  });

  renderTable(doc, rows, imgData, tableWidth);

  if (preview) {
    const blob = doc.output("blob");

    return {
      blob,
      url: URL.createObjectURL(blob),
    };
  }

  doc.save(`${fileName}_${saveDate}.pdf`);
}